import {ChangeDetectionStrategy, Component, inject, input, linkedSignal, signal} from '@angular/core';
import {toObservable, toSignal} from '@angular/core/rxjs-interop';
import {FormsModule, NgForm} from '@angular/forms';
import {ErrorService} from '@dv/kitadmin/core/errors';
import {updateRouterParams} from '@dv/kitadmin/core/uirouterrx';
import {DvbI18nPipe} from '@dv/kitadmin/i18n';
import {SearchEntityComponent} from '@dv/kitadmin/search';
import {
    DatepickerTextfieldComponent,
    DvLocalDateFormatPipe,
    handleResponseError,
    LoadingButtonComponent,
} from '@dv/shared/angular';
import {
    BetreuungsvereinbarnungKonfigurationApi,
} from '@dv/shared/backend/api/betreuungsvereinbarnung-konfiguration.api';
import {ReportingControllingApi} from '@dv/shared/backend/api/reporting-controlling.api';
import {
    JaxBetreuungsVereinbarungsKonfigurationType,
} from '@dv/shared/backend/model/jax-betreuungs-vereinbarungs-konfiguration-type';
import {
    checkPersisted,
    checkPresent,
    DvbDateUtil,
    DvbRestUtil,
    isNullish,
    ReportState,
    SearchResultEntry,
} from '@dv/shared/code';
import {DownloadService} from '@dv/shared/download';
import {TranslocoModule} from '@jsverse/transloco';
import moment from 'moment';
import {TooltipModule} from 'ngx-bootstrap/tooltip';
import {catchError, distinctUntilChanged, finalize, map, mergeMap, of, switchMap, tap} from 'rxjs';

@Component({
    selector: 'dv-betreuungsvereinbarungen-report',
    templateUrl: './betreuungsvereinbarungen-report.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        TranslocoModule,
        FormsModule,
        SearchEntityComponent,
        DatepickerTextfieldComponent,
        DvLocalDateFormatPipe,
        LoadingButtonComponent,
        DvbI18nPipe,
        TooltipModule,
    ],
})
export class BetreuungsvereinbarungenReportComponent {
    public kinderOrt = input<SearchResultEntry>();
    public gueltigAb = input<moment.Moment>();
    public gueltigBis = input<moment.Moment>();

    private errorService = inject(ErrorService);
    private betreuungsVereinbarungsKonfigurationService = inject(BetreuungsvereinbarnungKonfigurationApi);

    private downloadService = inject(DownloadService);
    private reportingControllingApi = inject(ReportingControllingApi);

    public readonly reportState = ReportState.CONTROLLING.BETREUUNGSVEREINBARUNGEN;

    public defaultGueltigAb: moment.Moment = DvbDateUtil.today().startOf('month');
    public defaultGueltigBis: moment.Moment = DvbDateUtil.today().endOf('month');

    public formKinderOrt = linkedSignal<SearchResultEntry | undefined>(this.kinderOrt);
    public formGueltigAb = linkedSignal<moment.Moment | undefined>(this.gueltigAb);
    public formGueltigBis = linkedSignal<moment.Moment | undefined>(this.gueltigBis);
    public formConfigType = linkedSignal<JaxBetreuungsVereinbarungsKonfigurationType | null>(() => {
        const types = this.vereinbarungTypes();
        if (types.length === 1) {
            return types[0];
        }

        return null;
    });

    private vereinbarungTypes$ = toObservable(this.formKinderOrt).pipe(
        distinctUntilChanged((a, b) => a?.id === b?.id),
        switchMap(kinderOrt => {
            if (isNullish(kinderOrt)) {
                return of([]);
            }

            return this.betreuungsVereinbarungsKonfigurationService.getTypesForKinderOrt$({
                kinderOrtId: checkPresent(kinderOrt.id),
            }).pipe(
                map(res => res.items.map(checkPersisted)),
                catchError((e: unknown) => {
                    handleResponseError(e, 'could not load BetreuungsVereinbarungsKonfigurationType');

                    return of([] as JaxBetreuungsVereinbarungsKonfigurationType[]);
                }),
            );
        }),
    );
    public vereinbarungTypes = toSignal(this.vereinbarungTypes$, {initialValue: []});

    public isLoading = signal(false);

    public constructor() {
        updateRouterParams(() => ({
            kinderOrte: [this.formKinderOrt()],
            gueltigAb: this.formGueltigAb(),
            gueltigBis: this.formGueltigBis(),
        }));
    }

    public submitForm(form: NgForm): void {
        this.errorService.clearAll();
        if (!form.valid) {
            this.errorService.addValidationError('ERRORS.ERR_INCOMPLETE_FORM');

            return;
        }
        this.downloadReport();
    }

    private downloadReport(): void {
        const request$ = this.reportingControllingApi.getBetreuungsvereinbarungen$({
            kinderOrtId: checkPresent(this.formKinderOrt()).id,
            betreuungsvereinbarungen: {
                gueltigAb: DvbRestUtil.momentToLocalDateChecked(this.formGueltigAb() ?? this.defaultGueltigAb),
                gueltigBis: DvbRestUtil.momentToLocalDateChecked(this.formGueltigBis() ?? this.defaultGueltigBis),
                betreuungsvereinbarungTypeId: this.formConfigType()?.id,
            },
        });

        request$.pipe(
            tap(() => this.isLoading.set(true)),
            mergeMap(tempBlob => this.downloadService.getBlob$(checkPresent(tempBlob)).pipe(
                tap(blob => this.downloadService.downloadBlob(blob, checkPresent(tempBlob).filename)),
            )),
            finalize(() => {
                this.isLoading.set(false);
            }),
        ).subscribe();
    }
}
